import type { paths } from '@connected/api-client/api.generated';
import type { FetchOptions } from 'openapi-fetch';

import { client } from '@connected/api-client';
import { useMediaManager } from '@connected/api-client/hooks/use-media-manager';
import { DEFAULT_IMAGE_QUALITY } from '@connected/ui/config/file';
import { blobToFile } from '@connected/ui/lib/utils';
import { useQuery } from '@tanstack/react-query';

import type { ElectricalProjectFile } from '@/hooks/use-electrical-project-files';

import { useElectricalProjectFiles } from '@/hooks/use-electrical-project-files';
import { logger } from '@/lib/logger';

export type UseElectricalProjectFileDownloadOptions = {
    query: FetchOptions<paths['/api/v2/electrical/projects/files/download']['get']>['params']['query'];
    enabled?: boolean;
};

export type UseElectricalProjectFileDownloadResult = {
    isLoading: boolean;
    isError: boolean;
    error: Error | null;
    file: File | null;
};

export function useElectricalProjectFileDownload({ query, enabled = true }: UseElectricalProjectFileDownloadOptions): UseElectricalProjectFileDownloadResult {
    const mediaManager = useMediaManager();

    const { generateMediaFileId, getElectricalProjectFile } = useElectricalProjectFiles();

    // Find the file metadata from the parent query. This is stable and SAFE.
    const fileMetadata = getElectricalProjectFile(query.ElectricalProjectFileId);

    const fileQuery = useQuery({
        queryKey: ['file-download', query.ElectricalProjectFileId, query.FileId ?? fileMetadata?.File?.FileId, query.ImageQuality],
        queryFn: async (): Promise<File | null> => {
            // GUARD CLAUSE: If we don't have metadata yet, we can't do anything.
            // React Query will re-run this function when fileMetadata changes.
            if (!fileMetadata) {
                console.warn(`File ${query.ElectricalProjectFileId} not found.`);
                return null;
            }

            // --- STEP 1: LOCAL-FIRST STRATEGY ---
            const mediaFileId = generateMediaFileId(fileMetadata);

            const localMediaFile = await mediaManager.getMediaFile(mediaFileId);

            if (localMediaFile) {
                logger.info(`File ${fileMetadata.ElectricalProjectFileId} found locally.`);
                return mediaManager.download(localMediaFile);
            }

            logger.info(`File ${fileMetadata.ElectricalProjectFileId} not found locally.`);

            // --- STEP 2: FALLBACK TO EXTERNAL FETCH ---
            const remoteFileId = fileMetadata.File?.FileId;
            if (remoteFileId) {
                logger.info(`File ${fileMetadata.ElectricalProjectFileId} not found locally, fetching from server.`);
                const response = await client.GET('/api/v2/electrical/projects/files/download', {
                    params: {
                        query: {
                            ElectricalProjectFileId: fileMetadata.ElectricalProjectFileId ?? '',
                            FileId: remoteFileId,
                            ImageQuality: query.ImageQuality ?? DEFAULT_IMAGE_QUALITY,
                        },
                    },
                    parseAs: 'blob',
                });

                if (response.error) {
                    logger.error(`Error fetching file ${fileMetadata.ElectricalProjectFileId} from server.`);
                    return null;
                }

                const newFile: ElectricalProjectFile = {
                    ElectricalProjectVersionId: fileMetadata.ElectricalProjectFileId,
                    ElectricalProjectFileId: fileMetadata.ElectricalProjectFileId,
                    Description: fileMetadata.Description,
                    Order: fileMetadata.Order,
                    HasFile: true,
                    File: {
                        FileId: remoteFileId,
                        Name: fileMetadata.File?.Name ?? '',
                        ContentType: fileMetadata.File?.ContentType ?? response.data.type,
                    },
                    Kind: fileMetadata.Kind,
                };

                const file = blobToFile(response.data, fileMetadata.File?.Name ?? '', fileMetadata.File?.ContentType ?? '');

                await mediaManager.upload(file, generateMediaFileId({
                    ElectricalProjectFileId: newFile.ElectricalProjectFileId!,
                    ElectricalProjectVersionId: newFile.ElectricalProjectVersionId!,
                }));

                // response.data is the Blob from the network
                return file;
            }

            // --- STEP 3: CANNOT FIND FILE ---
            logger.warn(`File ${fileMetadata.ElectricalProjectFileId} is not available locally and has no remote FileId.`);

            return null;
        },
        // Only enable the query if we have the metadata needed to perform a lookup.
        enabled: !!fileMetadata && enabled, // FIX: Might break stuff, i've got no clue
        staleTime: 0,
        gcTime: 0,
        // Keep previous file visible during refetch to prevent flicker when transitioning from local to server file
        placeholderData: previousData => previousData,
    });

    // Log errors to the console for now.
    if (fileQuery.isError) {
        logger.error(`Error downloading file: ${fileQuery.error}`);
    }

    return {
        isLoading: fileQuery.isLoading,
        isError: fileQuery.isError,
        error: fileQuery.error,
        file: fileQuery.data ?? null,
    };
}
